import type { ElementType } from 'react';
import React from 'react';

import { cx } from '@/utils/cx';

type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'caption'
  | 'small';

type FontWeight =
  | 'light'
  | 'normal'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'heavy'
  | 'black';

type FontFamily = 'body' | 'display' | 'sans' | 'serif' | 'mono';

type TextColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'muted'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'white'
  | 'black'
  | 'inherit';

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  /** The HTML element to render */
  as?: TypographyElement;
  /** Font weight */
  weight?: FontWeight;
  /** Font family */
  font?: FontFamily;
  /** Text color theme */
  color?: TextColor;
  /** Text content */
  children: React.ReactNode;
}

const fontFamilyMap: Record<FontFamily, string> = {
  body: 'font-body',
  display: 'font-display',
  sans: 'font-sans',
  serif: 'font-serif',
  mono: 'font-mono',
};

const fontWeightMap: Record<FontWeight, string> = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  heavy: 'font-heavy',
  black: 'font-black',
};

const colorMap: Record<TextColor, string> = {
  primary: 'text-fg-primary',
  secondary: 'text-fg-secondary',
  tertiary: 'text-fg-tertiary',
  muted: 'text-fg-quaternary',
  accent: 'text-fg-brand-secondary',
  success: 'text-fg-success-primary',
  warning: 'text-fg-warning-primary',
  error: 'text-fg-error-primary',
  white: 'text-fg-white',
  black: 'text-fg-primary',
  inherit: 'text-inherit',
};

const elementSizeMap: Record<TypographyElement, string> = {
  h1: 'text-display-lg md:text-display-xl lg:text-display-2xl',
  h2: 'text-display-sm md:text-display-md lg:text-display-lg',
  h3: 'text-xl md:text-display-sm lg:text-display-md',
  h4: 'text-lg md:text-xl lg:text-display-sm',
  h5: 'text-md md:text-lg lg:text-xl',
  h6: 'text-sm md:text-md lg:text-lg',
  p: 'text-md',
  span: 'text-md',
  div: 'text-md',
  label: 'text-sm font-medium',
  caption: 'text-xs',
  small: 'text-sm',
};

export const Typography: React.FC<TypographyProps> = ({
  as = 'p',
  weight = 'normal',
  font = 'body',
  color = 'primary',
  className,
  children,
  onClick,
  ...props
}) => {
  const Component = as as ElementType;

  const classes = cx(
    // Base typography styles
    elementSizeMap[as],
    fontFamilyMap[font],
    fontWeightMap[weight],
    colorMap[color],
    // Interactive styles
    onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
    // Custom classes
    className
  );

  return (
    <Component className={classes} onClick={onClick} {...props}>
      {children}
    </Component>
  );
};

// Convenience components for common use cases
export const Heading1: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h1' weight='bold' {...props} />
);

export const Heading2: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h2' weight='semibold' {...props} />
);

export const Heading3: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h3' weight='semibold' {...props} />
);

export const Heading4: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h4' weight='medium' {...props} />
);

export const Heading5: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h5' weight='medium' {...props} />
);

export const Heading6: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='h6' weight='medium' {...props} />
);

export const BodyText: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='p' {...props} />
);

export const Caption: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='span' color='muted' className='text-xs' {...props} />
);

export const Label: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='label' weight='medium' {...props} />
);

export const Small: React.FC<Omit<TypographyProps, 'as'>> = props => (
  <Typography as='small' color='muted' {...props} />
);

// Export the main component as default
export default Typography;
